import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useMutationRequestWorkLogChanges } from "../../../../query/workLog/mutations/useMutationRequestWorkLogChanges";
import { PublicCommentField } from "../../components/PublicCommentField";

type FormData = {
  comment: string;
};

type Props = {
  workLog: IWorkLog;
  contractorName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalRequestChanges: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [requestChanges, requestChangesMeta] =
    useMutationRequestWorkLogChanges();

  const handleRequestChanges = useCallback(
    async (formData: FormData) => {
      if (!formData.comment.trim()) {
        openSnackbar({
          type: "warning",
          message: "Please provide details about the requested changes"
        });
        return;
      }

      requestChanges(
        { workLogId: workLog.workLogId, comment: formData.comment },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: "Changes requested successfully"
            });
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to request changes. Please try again."
            });
          }
        }
      );
    },
    [openSnackbar, modalProps, requestChanges, workLog.workLogId]
  );

  return (
    <WSModal title="Request changes" {...modalProps}>
      <WSForm<FormData>
        onSubmit={handleRequestChanges}
        defaultValues={{ comment: "" }}
      >
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to request changes?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About requesting changes on work logs"
            icon="info-circle"
          >
            This sends the work log back to the contractor for corrections.
            They&apos;ll make changes and resubmit for your approval. Provide
            specific guidance below about what needs to change.
          </WSAlert>

          <PublicCommentField
            label="Comment *"
            description={`Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the request`}
            placeholder="Provide specific details about the request"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                label: "Yes, request changes",
                loading: requestChangesMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalRequestChanges = () =>
  useWSModalCustom(ModalRequestChanges);
